import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { StatusBadge } from "../components/badges";
import { UnsavedDraftDot } from "../components/UnsavedDraftDot";
import { MAX_BOX_SLUG_LENGTH, matchesSearch } from "../lib/boxes";
import { amountColumnHeader, useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { slugify } from "../lib/slug";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { useTenantRole } from "../lib/useTenantRole";

export function BoxesPage() {
  // Viewers browse; box creation and enablement are editor actions.
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const trpc = useTRPC();
  const currency = useCurrency();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim());

  const boxes = useQuery(
    trpc.boxes.list.queryOptions(
      { search: debouncedSearch || undefined },
      // Keep the previous rows visible while a new search resolves.
      { placeholderData: (previous) => previous },
    ),
  );
  // The boxes that are Shopify's: every rule box and every box on the channel; empty where no
  // store is linked.
  const shopifyChannels = useQuery(trpc.shopify.boxChannels.queryOptions());
  const onShopify = new Set((shopifyChannels.data ?? []).map((c) => c.boxId));
  // Placeholder data means the rows on screen belong to the previous search.
  const isStale = boxes.isPlaceholderData;

  // The template list is small and unpaginated, so the same term filters it in memory
  // rather than round-tripping — the search box covers the whole page either way.
  const templates = useQuery(trpc.boxTemplates.list.queryOptions());
  const templateRows = (templates.data ?? []).filter((box) => matchesSearch(box, debouncedSearch));

  const invalidateTemplates = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.boxTemplates.list.pathKey() });
  };
  const enable = useMutation(
    trpc.boxTemplates.enable.mutationOptions({ onSuccess: invalidateTemplates }),
  );
  const disable = useMutation(
    trpc.boxTemplates.disable.mutationOptions({ onSuccess: invalidateTemplates }),
  );

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const create = useMutation(
    trpc.boxes.create.mutationOptions({
      onSuccess: (box) => {
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
        setShowCreate(false);
        void navigate({
          to: "/tenants/$tenantId/boxes/$boxId",
          params: { tenantId, boxId: box.id },
        });
      },
    }),
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Boxes</h1>
          <p className="text-sm text-muted-foreground">
            Everything that can appear on this environment's storefront.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Clear and the hint sit left of the input so neither nudges the field. */}
          {isStale && <span className="text-sm text-muted-foreground">Searching…</span>}
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear
            </Button>
          )}
          <Input
            className="w-64"
            type="search"
            placeholder="Search name, slug or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {canEdit && (
            <Button type="button" onClick={() => setShowCreate(true)}>
              <Plus />
              New box
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Your boxes</h2>
          <p className="text-sm text-muted-foreground">
            Publish a drop table version to make a box openable.
          </p>
        </div>

        <Card>
          <CardContent>
            <Table
              aria-busy={isStale}
              className={`transition-opacity ${isStale ? "opacity-60" : ""}`}
            >
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Version</TableHead>
                  <TableHead className="text-right">
                    {amountColumnHeader("Price", currency)}
                  </TableHead>
                  <TableHead className="text-right">Opened</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {boxes.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      {debouncedSearch
                        ? `No boxes match “${debouncedSearch}”.`
                        : "No boxes yet. Create one, define its drop table and publish."}
                    </TableCell>
                  </TableRow>
                )}
                {boxes.data?.map((box) => (
                  <TableRow key={box.id}>
                    <TableCell>
                      {box.imageUrl ? (
                        // max-w-none: preflight's `max-width: 100%` would otherwise let the cell squeeze
                        // the width while the height stays fixed, so the thumbnail stopped being square.
                        <img
                          className="size-8 max-w-none rounded-md border object-cover"
                          src={box.imageUrl}
                          alt=""
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/tenants/$tenantId/boxes/$boxId"
                        params={{ tenantId, boxId: box.id }}
                        className="font-medium hover:underline"
                      >
                        {box.name}
                      </Link>
                      <UnsavedDraftDot boxId={box.id} />
                      {onShopify.has(box.id) && (
                        <Badge variant="outline" className="ml-2">
                          Shopify
                        </Badge>
                      )}
                      {box.type === "COMMUNITY" && (
                        // A forge box: built by a shopper (the creator), or mirrored from boxed with none.
                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline">Forge</Badge>
                          {box.creatorDisplayName && <span>by {box.creatorDisplayName}</span>}
                          {box.deletedAt && <span>deleted by its creator</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {box.slug}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={box.status} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {box.currentVersion || "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {box.priceCents ? formatAmount(box.priceCents, currency) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatNumber(box.openedCount)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(box.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Box templates</h2>
          <p className="text-sm text-muted-foreground">
            Boxes authored by Loot OS and shared across tenants. Enabling one adds it to this
            environment's storefront; they are authored on the Box templates page.
          </p>
        </div>

        <Card>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">
                    {amountColumnHeader("Price", currency)}
                  </TableHead>
                  <TableHead className="text-right">Opened (all tenants)</TableHead>
                  <TableHead className="text-right">Enabled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templateRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      {debouncedSearch
                        ? `No box templates match “${debouncedSearch}”.`
                        : "No box templates yet."}
                    </TableCell>
                  </TableRow>
                )}
                {templateRows.map((box) => (
                  <TableRow key={box.id}>
                    <TableCell>
                      {box.imageUrl ? (
                        // max-w-none: preflight's `max-width: 100%` would otherwise let the cell squeeze
                        // the width while the height stays fixed, so the thumbnail stopped being square.
                        <img
                          className="size-8 max-w-none rounded-md border object-cover"
                          src={box.imageUrl}
                          alt=""
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {/* Leaves tenant scope — box templates are edited globally. */}
                      <Link
                        to="/templates/$boxId"
                        params={{ boxId: box.id }}
                        className="font-medium hover:underline"
                        title="Edit in the Loot OS box templates"
                      >
                        {box.name}
                      </Link>
                      <UnsavedDraftDot boxId={box.id} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={box.status} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatAmount(box.priceCents, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatNumber(box.openedCount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={box.enabled}
                        onCheckedChange={(checked) => {
                          if (checked) enable.mutate({ boxId: box.id });
                          else disable.mutate({ boxId: box.id });
                        }}
                        disabled={
                          !canEdit ||
                          enable.isPending ||
                          disable.isPending ||
                          (!box.enabled && box.status !== "active")
                        }
                        title={
                          !box.enabled && box.status !== "active" ? "box is not active" : undefined
                        }
                        aria-label={`${box.enabled ? "Disable" : "Enable"} ${box.name}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New box</DialogTitle>
            <DialogDescription>
              Give the box a name and a URL-safe slug. You'll define its drop table next.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ name, slug, imageUrl });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="box-name">Name</Label>
              <Input
                id="box-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value, MAX_BOX_SLUG_LENGTH));
                }}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="box-slug">Slug</Label>
              <Input
                id="box-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                pattern="[a-z0-9][a-z0-9\-]*"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="box-image-url">Image URL (optional)</Label>
              <Input
                id="box-image-url"
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create box"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
